import { useEffect, useRef, useState } from 'react';
import Icon from './Icon.jsx';

const ACCEPT = 'image/jpeg,image/png,image/webp';

/**
 * Image field with a live preview and client-side validation. Emits the chosen
 * File (or null when cleared) via `onPick`; the parent decides when to upload.
 * `existingUrl` shows the currently stored image before a new one is picked.
 */
export default function ImagePicker({ onPick, existingUrl, maxBytes = 1024 * 1024, label = 'Choose image' }) {
  const inputRef = useRef(null);
  const [preview, setPreview] = useState(null);
  const [error, setError] = useState('');

  // Revoke the object URL when it changes or on unmount.
  useEffect(() => () => preview && URL.revokeObjectURL(preview), [preview]);

  const accept = (file) => {
    setError('');
    if (!file) return;
    if (!ACCEPT.split(',').includes(file.type)) {
      return setError('Use a JPG, PNG or WebP image.');
    }
    if (file.size > maxBytes) {
      return setError(`Image must be under ${Math.round(maxBytes / 1024 / 1024) || 1} MB.`);
    }
    if (preview) URL.revokeObjectURL(preview);
    setPreview(URL.createObjectURL(file));
    onPick(file);
  };

  const shownUrl = preview || existingUrl;

  return (
    <div className="image-picker">
      <div className="image-picker-preview" aria-hidden="true">
        {shownUrl ? <img src={shownUrl} alt="" /> : <Icon name="image" size={26} />}
      </div>
      <div className="stack-sm">
        <button type="button" className="btn btn-sm" onClick={() => inputRef.current?.click()}>
          <Icon name="upload" />
          {shownUrl ? 'Change image' : label}
        </button>
        {preview && (
          <button
            type="button"
            className="btn btn-sm btn-ghost"
            onClick={() => {
              URL.revokeObjectURL(preview);
              setPreview(null);
              onPick(null);
            }}
          >
            Clear
          </button>
        )}
        {error ? <span className="field-error">{error}</span> : <span className="tiny dim">JPG, PNG or WebP · up to 1 MB</span>}
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={ACCEPT}
        className="sr-only"
        onChange={(e) => {
          accept(e.target.files?.[0]);
          e.target.value = '';
        }}
      />
    </div>
  );
}
